'use client'

import {
  type ChangeEventHandler,
  type ComponentProps,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type ReactNode,
  useId,
  useRef,
  useState,
} from 'react'
import clsx from 'clsx'
import { CrossIcon } from '../../icons/CrossIcon'
import { LockIcon } from '../../icons/LockIcon'
import { WarningTriangleIcon } from '../../icons/WarningTriangleIcon'
import { xStack, yStack } from '../../patterns/stack'
import { mergeRefs } from '../../utils/mergeRefs'
import { useHighlightAnimation } from '../../utils/useHighlightAnimation'
import { LoadingDots } from '../LoadingDots/LoadingDots'
import { Text } from '../Text/Text'
import { sprinkles } from '../../theme/sprinkles.css'
import { tokens } from '../../theme/tokens.css'
import {
  deleteButton,
  disableNumberInputStyle,
  input,
  inputLabel,
  inputMeta,
  labelScale,
  labelWrapper,
  upperCaseInputStyle,
  wrapper,
} from './TextField.css'

type BaseInputProps = Omit<
  ComponentProps<'input'>,
  'onAnimationStart' | 'onDragStart' | 'onDragEnd' | 'onDrag' | 'onChange' | 'size'
>

export type TextFieldProps = BaseInputProps & {
  label: string
  meta?: string
  size?: 'small' | 'medium' | 'large'
  suffix?: string
  warning?: boolean
  loading?: boolean
  message?: string
  onValueChange?: (value: string) => void
  onClear?: () => void
  upperCaseInput?: boolean
  disableNumberArrows?: boolean
  labelProps?: ComponentProps<'label'>
}

export const TextField = (props: TextFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const {
    ref,
    defaultValue,
    label,
    meta,
    size = 'medium',
    suffix,
    warning = false,
    loading = false,
    message,
    id,
    onValueChange,
    onKeyDown,
    onClear,
    upperCaseInput,
    disableNumberArrows,
    className,
    labelProps,
    ...inputProps
  } = props
  const [value, setValue] = useState(defaultValue || '')
  const { highlight, animationProps } = useHighlightAnimation<HTMLDivElement>()
  const generatedId = useId()
  const identifier = id || generatedId
  const metaId = `${identifier}-meta`

  const inputValue = inputProps.value || value
  const showMeta = Boolean(inputValue && meta)
  const describedBy = clsx(showMeta && metaId, inputProps['aria-describedby']) || undefined

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const newValue = event.target.value
    setValue(newValue)
    onValueChange?.(newValue)
  }

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    highlight(event)
    onKeyDown?.(event)
  }

  const handleClickDelete: MouseEventHandler<HTMLButtonElement> = () => {
    onClear?.()
    const newValue = ''
    setValue(newValue)
    onValueChange?.(newValue)
  }

  const handleClickWrapper: MouseEventHandler<HTMLDivElement> = () => {
    inputRef.current?.focus()
  }

  let endIcon: ReactNode = null
  if (warning) {
    endIcon = <WarningTriangleIcon size="1.5em" color={tokens.colors.signal.amber.element} />
  } else if (loading) {
    endIcon = <LoadingDots />
  } else if (inputValue) {
    if (inputProps.disabled || inputProps.readOnly) {
      endIcon = <LockIcon size="1.5rem" color={tokens.colors.text.opaque.secondary} />
    } else {
      endIcon = (
        <button className={deleteButton} type="button" onClick={handleClickDelete} aria-hidden={true} tabIndex={-1}>
          <CrossIcon size="1.5rem" />
        </button>
      )
    }
  }

  return (
    <div className={yStack({ gap: 'xxs' })}>
      <div
        className={clsx(wrapper[size], className)}
        {...animationProps}
        data-active={!!inputValue || !!inputProps.placeholder}
        data-warning={warning}
        data-readonly={inputProps.readOnly ? '' : undefined}
        data-hidden={inputProps.hidden ?? undefined}
        onClick={handleClickWrapper}
      >
        <div className={labelWrapper[size]}>
          <label
            className={clsx(inputLabel, labelScale[size])}
            htmlFor={identifier}
            data-disabled={inputProps.disabled}
            {...labelProps}
          >
            {label}
          </label>

          {showMeta && (
            <span id={metaId} className={clsx(inputMeta, labelScale[size])} role="status">
              {meta}
            </span>
          )}
        </div>

        <div
          className={xStack({
            alignItems: meta ? 'flex-end' : 'center',
            gap: 'xs',
            paddingRight: 'md',
            width: '100%',
          })}
        >
          <input
            className={clsx(
              input[size],
              upperCaseInput && upperCaseInputStyle,
              disableNumberArrows && disableNumberInputStyle,
            )}
            {...inputProps}
            id={identifier}
            ref={mergeRefs(inputRef, ref)}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            value={inputValue}
            aria-label={label}
            aria-describedby={describedBy}
          />

          {suffix && inputValue && (
            <Text as="span" size={size === 'large' ? 'xl' : 'lg'} color="textSecondary">
              {suffix}
            </Text>
          )}

          {endIcon}
        </div>
      </div>

      {message && (
        <Text className={sprinkles({ paddingLeft: 'md' })} size="xs">
          {message}
        </Text>
      )}
    </div>
  )
}
